import React from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';

import { usePermission } from '../../hooks/usePermission';
import type { PermAction } from '../../utils/permissions';

export type PermissionMode = 'hide' | 'disable';

export interface PermissionButtonProps extends AntButtonProps {
  /**
   * Optional RBAC action gate for this button.
   *
   * - If omitted, the button is treated as "neutral" (always allowed).
   * - If provided, the user must have `action` on `path` (or current route if path is omitted).
   *
   * NOTE: This is UI/UX-only. Backend must still enforce authorization.
   */
  action?: PermAction;
  /**
   * Optional path to check permissions against.
   * If omitted, the current route path is used (via usePermission()).
   */
  path?: string;
  /**
   * If unauthorized:
   * - 'disable' (default): render the button disabled
   * - 'hide': render nothing
   */
  mode?: PermissionMode;
}

/**
 * Permission-aware wrapper around Ant Design <Button />.
 *
 * This component is intentionally "opt-in":
 * - Most buttons can remain neutral (no `action` prop).
 * - For privileged actions, pass `action="add|edit|delete|read"` and optionally `path`.
 */
const Button = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, PermissionButtonProps>(
  ({ action, path, mode = 'disable', disabled, ...rest }, ref) => {
    const { can } = usePermission(path);

    // Neutral button: no action gate
    const allowed = action ? can(action) : true;

    if (!allowed && mode === 'hide') {
      return null;
    }

    // If permission denies, force disabled (but still allow caller to disable as well).
    const effectiveDisabled = Boolean(disabled) || (!allowed && mode === 'disable');

    return <AntButton ref={ref} disabled={effectiveDisabled} {...rest} />;
  }
);

Button.displayName = 'Button';

export default Button;

